import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Provider, ProviderTokenStatus } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { Field, FormError, useSubmit } from '../../components/Field';

const TOKENS_KEY = ['provider-tokens'] as const;

const SOURCES: Readonly<Record<Provider, { label: string; where: string }>> = Object.freeze({
  huggingface: { label: 'Hugging Face', where: 'huggingface.co → Settings → Access Tokens (read access is enough)' },
  civitai: { label: 'Civitai', where: 'civitai.com → Account settings → API Keys' },
  comfyorg: { label: 'Comfy.org (API nodes)', where: 'platform.comfy.org → API Keys. Runs cloud nodes such as Seedance; they use your Comfy.org credits.' },
  anthropic: { label: 'Anthropic (workflow tab)', where: 'console.anthropic.com → API Keys. Claude in the Workflow tab runs on it; Anthropic bills you per use.' },
});

function TokenRow({ status }: { status: ProviderTokenStatus }) {
  const queryClient = useQueryClient();
  const { label, where } = SOURCES[status.provider];
  const [token, setToken] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: TOKENS_KEY });
  const save = useSubmit(() => api.setProviderToken(status.provider, token), () => { setToken(''); void refresh(); });
  const remove = useMutation({ mutationFn: () => api.removeProviderToken(status.provider), onSuccess: refresh });
  const since = status.updatedAt ? new Date(status.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
  return (
    <fieldset className="token-row" aria-label={label}>
      <legend>{label}</legend>
      <p className={status.set ? 'success' : 'muted'}>{status.set ? `Set (since ${since})` : 'Not set'}</p>
      <form onSubmit={save.submit} noValidate className="token-form">
        <Field label={`${label} token`} name={status.provider} type="password" value={token} onChange={setToken} autoComplete="off" error={save.fieldErrors.token} hint={where} />
        <FormError message={save.formError} />
        <div className="token-actions">
          <button type="submit" disabled={save.pending || token.trim() === ''} aria-label={`Save ${label} token`}>Save</button>
          {status.set && (
            <button type="button" className="button-quiet" aria-label={`Remove ${label} token`} disabled={remove.isPending} onClick={() => { remove.mutate(); }}>Remove</button>
          )}
        </div>
        {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
      </form>
    </fieldset>
  );
}

/** Hugging Face / Civitai tokens for model downloads — write-only, stored encrypted. */
export function ModelSourcesPanel() {
  const tokens = useQuery({ queryKey: TOKENS_KEY, queryFn: api.providerTokens });
  return (
    <section className="panel" aria-labelledby="sources-title">
      <h2 id="sources-title">Model sources</h2>
      <p className="muted">Needed for gated or rate-limited downloads. Couch stores them encrypted and only ever sends them to your own machines.</p>
      {tokens.isPending && <p role="status">Loading…</p>}
      {tokens.isError && <p role="alert" className="form-error">{errorMessage(tokens.error)}</p>}
      {tokens.data?.tokens.map((t) => <TokenRow key={t.provider} status={t} />)}
    </section>
  );
}
